import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

import { checkNonZeroArray } from '../../helper';
import { DashboardType, MetricsView, MetricsType } from '../../types';
import { CAType } from '../config';
import { appliedCAIdsAtom, caTypeAtom, severityFiltersAtom } from '../state';

export const isMetricsSectionOpenAtom = atom(false);
export const metricsViewAtom = atomWithStorage(
  'ca_metrics_view',
  MetricsView.Severity,
);
export const timeViewLabels = [
  '0 to 3 Days',
  '3+ to 7 Days',
  '7+ to 14 Days',
  '14+ to 28 Days',
  '28+ Days',
];

export const vulnMetricsDataAtom = atom(async (get) => {
  const isOpen = get(isMetricsSectionOpenAtom);
  if (isOpen) {
    const companyId = get(companyIdAtom);
    const selectedCAIds = get(appliedCAIdsAtom);

    try {
      if (companyId && companyId !== -1) {
        const params = {
          company_id: companyId,
          host_type: DashboardType.CA,
        };
        // Keep large selected-ID lists out of the metrics URL as well.
        const hasSelectedIds = selectedCAIds?.length > 0;
        const requestData = hasSelectedIds
          ? { obj_id_list: selectedCAIds }
          : null;

        const responseData = await callApi(
          'dashboard/metrics',
          hasSelectedIds ? 'POST' : 'GET',
          params,
          requestData,
          null,
          'dashboard/metrics:ca-vuln-metrics',
        );
        return responseData?.data;
      }
    } catch (error) {
      console.error(error);
    }
  }
  return undefined;
});

export const openVulnMetricsBySeverityAverageDayTotalVulnAtom = atom(
  async (get) => {
    const data: any = await get(vulnMetricsDataAtom);
    const caType = get(caTypeAtom);
    const severityFilters = get(severityFiltersAtom);
    if (data) {
      return getVulnMetrics(
        data.severityBasedData,
        caType,
        MetricsType.Open,
        'Days',
        severityFilters,
      );
    }
    return undefined;
  },
);

export const openVulnMetricsBySeverityCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      caType,
      MetricsType.Open,
      'Count',
      severityFilters,
    );
  }
  return undefined;
});

export const closeVulnMetricsBySeverityAverageDayTotalVulnAtom = atom(
  async (get) => {
    const data: any = await get(vulnMetricsDataAtom);
    const caType = get(caTypeAtom);
    const severityFilters = get(severityFiltersAtom);
    if (data) {
      return getVulnMetrics(
        data.severityBasedData,
        caType,
        MetricsType.Closed,
        'Days',
        severityFilters,
      );
    }
    return undefined;
  },
);

export const closedVulnMetricsBySeverityCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      caType,
      MetricsType.Closed,
      'Count',
      severityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsByTimeCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      caType,
      MetricsType.Open,
      severityFilters,
    );
  }
  return undefined;
});

export const closedVulnMetricsByTimeCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      caType,
      MetricsType.Closed,
      severityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsTooltipAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const caType = get(caTypeAtom);
  if (data) {
    return getVulnMetricsTooltips(
      data.severityBasedData,
      caType,
      MetricsType.Open,
      'Days',
    );
  }
  return [];
});

export const closedVulnMetricsTooltipAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const caType = get(caTypeAtom);
  if (data) {
    return getVulnMetricsTooltips(
      data.severityBasedData,
      caType,
      MetricsType.Closed,
      'Days',
    );
  }
  return [];
});

function getVulnMetrics(
  sourceData: any,
  caType: CAType,
  metricsType: MetricsType,
  propertyName: string,
  severityFilter: any,
) {
  const serverNode = sourceData[CAType.Server][metricsType];
  const workstationNode = sourceData[CAType.Workstation][metricsType];

  const serverValues: any[] = Object.values(serverNode);
  const workstationValues: any[] = Object.values(workstationNode);
  const serverData = serverValues.map((x) => x[propertyName]);
  const workstationData = workstationValues.map((x) => x[propertyName]);
  const resultsArray: number[][] = [];
  if (
    (caType === CAType.Server || caType === CAType.All) &&
    checkNonZeroArray(serverData)
  )
    resultsArray.push(serverData);
  if (
    (caType === CAType.Workstation || caType === CAType.All) &&
    checkNonZeroArray(workstationData)
  )
    resultsArray.push(workstationData);

  const averageDaysOpenClose = getVulnMetricsAverageDaysOpenClose(
    sourceData,
    caType,
    metricsType,
    severityFilter,
  );
  const totalVulnsCount = getVulnMetricsTotalVulnsCount(
    sourceData,
    caType,
    metricsType,
    severityFilter,
  );
  return {
    metricsData: resultsArray,
    averageDaysOpenClose: averageDaysOpenClose,
    totalVulnsCount: totalVulnsCount,
  };
}

function getVulnMetricsTotalVulnsCount(
  sourceData: any,
  caType: CAType,
  metricsType: MetricsType,
  severityFilters: string[],
) {
  const filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    caType,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  return totalCount;
}

function getVulnMetricsAverageDaysOpenClose(
  sourceData: any,
  caType: CAType,
  metricsType: MetricsType,
  severityFilters: any,
) {
  const filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    caType,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalDays = validVulnerabilities.reduce(
    (sum, v) => sum + v.Days * v.Count,
    0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  const averageDaysOpenClose = totalCount > 0 ? totalDays / totalCount : 0;
  return averageDaysOpenClose.toFixed(1);
}

function filterMetricsBySeverity(
  sourceData: any,
  caType: CAType,
  metricsType: MetricsType,
  severityFilters: any,
) {
  const serverNode = sourceData[CAType.Server][metricsType];
  const workstationNode = sourceData[CAType.Workstation][metricsType];

  const severities = ['Urgent', 'Critical', 'Medium', 'Minimal', 'Serious'];

  const resultArray: any[] = [];
  severities.forEach((severity) => {
    if (severityFilters.includes(severity)) {
      if (caType === CAType.Server || caType === CAType.All) {
        const serverValues = serverNode[severity];
        resultArray.push(serverValues);
      }
      if (caType === CAType.Workstation || caType === CAType.All) {
        const workstationValues = workstationNode[severity];
        resultArray.push(workstationValues);
      }
    }
  });
  return resultArray;
}

function getVulnMetricsByTimeData(
  sourceData: any,
  caType: CAType,
  metricsType: MetricsType,
  severityFilter: any,
) {
  const serverData: number[] = [];
  const workstationData: number[] = [];

  severityFilter.forEach((severity) => {
    const serverNode = sourceData[CAType.Server][metricsType][severity];
    const workstationNode =
      sourceData[CAType.Workstation][metricsType][severity];
    const serverValues: any[] = Object.values(serverNode);
    const workstationValues: any[] = Object.values(workstationNode);

    for (let i = 0; i < serverValues.length; i++) {
      if (serverData[i]) serverData[i] += serverValues[i];
      else serverData[i] = serverValues[i];

      if (workstationData[i]) workstationData[i] += workstationValues[i];
      else workstationData[i] = workstationValues[i];
    }
  });

  const resultsArray: number[][] = [];
  if (
    (caType === CAType.Server || caType === CAType.All) &&
    checkNonZeroArray(serverData)
  )
    resultsArray.push(serverData);
  if (
    (caType === CAType.Workstation || caType === CAType.All) &&
    checkNonZeroArray(workstationData)
  )
    resultsArray.push(workstationData);
  return resultsArray;
}

function getVulnMetricsTooltips(
  sourceData: any,
  caType: CAType,
  metricsType: MetricsType,
  propertyName: string,
) {
  const serverNode = sourceData[CAType.Server][metricsType];
  const workstationNode = sourceData[CAType.Workstation][metricsType];

  const serverValues: any[] = Object.values(serverNode);
  const workstationValues: any[] = Object.values(workstationNode);

  const serverData = serverValues.map((x) => x[propertyName]);
  const workstationData = workstationValues.map((x) => x[propertyName]);

  const toolTipLabels: string[] = [];
  if (
    (caType === CAType.Server || caType === CAType.All) &&
    checkNonZeroArray(serverData)
  ) {
    toolTipLabels.push('Servers');
  }
  if (
    (caType === CAType.Workstation || caType === CAType.All) &&
    checkNonZeroArray(workstationData)
  ) {
    toolTipLabels.push(CAType.Workstation);
  }
  return toolTipLabels;
}
